/**
 * Dataset das telas de catálogo/estoque (Task 11).
 *
 * Duas fontes, cada uma com o seu estado declarado (mesmo vocabulário do
 * dashboard: `live` vs `awaiting_contract`):
 * - `products`  — catálogo em duas camadas da Plataforma (0019 + 0030);
 * - `snapshot`  — lado operacional do Bling (real ou simulador).
 *
 * As divergências NÃO vêm do servidor: são derivadas aqui, com a função pura
 * `computeCatalogDivergences`. É por isso que a lista da tela e o contador do
 * produto nunca discordam — é o mesmo cálculo, uma vez só.
 *
 * `snapshot === null` significa **fonte do operacional ausente**, não "nenhuma
 * divergência": sem o lado do Bling a tela declara que não há comparação
 * possível (jamais "tudo sincronizado").
 */
import { computeCatalogDivergences, type CatalogDivergence } from "../../../../packages/catalog/src/index.js";
import type { DashboardSourceState } from "../dashboard/kpis.js";
import type { BlingCatalogSnapshot, BlingSyncLogEntry, PlatformCatalogProduct } from "../api/types.js";

export interface CatalogSources {
  platformCatalog: DashboardSourceState;
  blingSnapshot: DashboardSourceState;
  /** Eventos de `bling_sync_log` — origem citada por cada divergência. */
  blingSyncLog: DashboardSourceState;
}

export interface CatalogDataset {
  products: PlatformCatalogProduct[];
  snapshot: BlingCatalogSnapshot | null;
  /**
   * Eventos de sync publicados pelo ambiente. `null` = rota ainda não
   * publicada aqui: o drill-down da divergência DIZ que o evento de origem não
   * está disponível, em vez de levar a uma tela que ignora o identificador.
   */
  syncLog: BlingSyncLogEntry[] | null;
  sources: CatalogSources;
}

export function emptyCatalogDataset(): CatalogDataset {
  return {
    products: [],
    snapshot: null,
    syncLog: null,
    // Nada buscado ainda: declarar `live` faria a tela vazia parecer "nenhum
    // produto" em vez de "nada carregado".
    sources: { platformCatalog: "awaiting_contract", blingSnapshot: "awaiting_contract", blingSyncLog: "awaiting_contract" }
  };
}

/** `null` = sem o lado operacional não existe comparação (nunca "zero divergências"). */
export function catalogDivergencesOf(dataset: CatalogDataset): CatalogDivergence[] | null {
  if (!dataset.snapshot) return null;
  return computeCatalogDivergences(dataset.products, dataset.snapshot);
}

export function catalogProductById(dataset: CatalogDataset, productId: string): PlatformCatalogProduct | null {
  return dataset.products.find((product) => product.productId === productId) ?? null;
}

/**
 * Evento de sync citado por uma divergência.
 *
 * Três respostas DIFERENTES, nunca confundidas pela tela de destino:
 * - `"source_absent"` = a rota de eventos não está publicada neste ambiente;
 * - `"not_found"`     = a rota existe e NÃO tem esse id (o estado operacional
 *   veio de um evento que não está mais publicado) — dizer isso é melhor que
 *   mostrar uma tela vazia como se o link estivesse certo;
 * - o registro, quando existe.
 */
export function syncLogEntryById(
  dataset: CatalogDataset,
  syncLogId: string
): BlingSyncLogEntry | "source_absent" | "not_found" {
  if (dataset.syncLog === null) return "source_absent";
  return dataset.syncLog.find((entry) => entry.id === syncLogId) ?? "not_found";
}

/**
 * Texto único da credencial do Bling, usado pelo selo da tela e pelo relatório.
 * "Verde em código, aguardando secret": o caminho real existe e é exercitado;
 * só falta a credencial para apontar ao Bling de produção.
 */
export function blingSourceLabel(snapshot: BlingCatalogSnapshot | null): string {
  if (!snapshot) return "Fonte do operacional (Bling) não publicada neste ambiente";
  if (snapshot.source === "bling") return "Comparação contra o Bling real";
  return "Comparação contra o simulador do Bling — verde em código, aguardando secret BLING_ACCESS_TOKEN";
}
